/** sessionStorage keys for multi-vendor checkout progress */
export const STORAGE_PENDING_CHECKOUTS = 'checkoutPendingCheckouts';
export const STORAGE_CURRENT_INDEX = 'checkoutCurrentIndex';
/** @deprecated kept so cancel clears old keys */
export const STORAGE_PENDING_URLS = 'checkoutPendingUrls';

/**
 * Normalize create-checkout API response into a checkouts array.
 * Supports new `checkouts` and legacy `checkoutSessionUrls`.
 */
export function normalizeCheckoutsResponse(data) {
  if (!data || typeof data !== 'object') return [];

  if (Array.isArray(data.checkouts) && data.checkouts.length > 0) {
    return data.checkouts.map((c) => ({
      vendorId: c.vendorId ?? null,
      vendorName: c.vendorName || 'Vendor',
      booth: c.booth || null,
      itemCount: c.itemCount ?? (Array.isArray(c.itemIds) ? c.itemIds.length : 0),
      subtotal: typeof c.subtotal === 'number' ? c.subtotal : parseFloat(c.subtotal) || 0,
      itemIds: Array.isArray(c.itemIds) ? c.itemIds.map(Number) : [],
      checkoutUrl: c.checkoutUrl || '',
    })).filter((c) => c.checkoutUrl);
  }

  const urls = data.checkoutSessionUrls;
  if (Array.isArray(urls) && urls.length > 0) {
    return urls.filter(Boolean).map((url, i) => ({
      vendorId: null,
      vendorName: `Vendor ${i + 1}`,
      booth: null,
      itemCount: 0,
      subtotal: 0,
      itemIds: [],
      checkoutUrl: url,
    }));
  }

  return [];
}

export function savePendingCheckouts(checkouts, currentIndex = 0) {
  sessionStorage.setItem(STORAGE_PENDING_CHECKOUTS, JSON.stringify(checkouts));
  sessionStorage.setItem(STORAGE_CURRENT_INDEX, String(currentIndex));
  sessionStorage.removeItem(STORAGE_PENDING_URLS);
}

export function loadPendingCheckouts() {
  const raw = sessionStorage.getItem(STORAGE_PENDING_CHECKOUTS);
  if (!raw) {
    // migrate legacy URL list if present
    const legacy = sessionStorage.getItem(STORAGE_PENDING_URLS);
    if (!legacy) return { checkouts: [], currentIndex: 0 };
    try {
      const urls = JSON.parse(legacy);
      const checkouts = normalizeCheckoutsResponse({ checkoutSessionUrls: urls });
      const indexStr = sessionStorage.getItem(STORAGE_CURRENT_INDEX);
      return {
        checkouts,
        currentIndex: indexStr != null ? parseInt(indexStr, 10) || 0 : 0,
      };
    } catch {
      return { checkouts: [], currentIndex: 0 };
    }
  }
  try {
    const checkouts = JSON.parse(raw);
    const indexStr = sessionStorage.getItem(STORAGE_CURRENT_INDEX);
    return {
      checkouts: Array.isArray(checkouts) ? checkouts : [],
      currentIndex: indexStr != null ? parseInt(indexStr, 10) || 0 : 0,
    };
  } catch {
    return { checkouts: [], currentIndex: 0 };
  }
}

export function clearPendingCheckouts() {
  sessionStorage.removeItem(STORAGE_PENDING_CHECKOUTS);
  sessionStorage.removeItem(STORAGE_CURRENT_INDEX);
  sessionStorage.removeItem(STORAGE_PENDING_URLS);
}

/** After paying index `paidIndex`, progress state for the success UI. */
export function getCheckoutProgress(checkouts, paidIndex) {
  const total = Array.isArray(checkouts) ? checkouts.length : 0;
  const paid = Math.min(Math.max(paidIndex + 1, 0), total);
  const paidCheckout = total > 0 && paidIndex >= 0 && paidIndex < total ? checkouts[paidIndex] : null;
  const nextIndex = paidIndex + 1;
  const nextCheckout = nextIndex < total ? checkouts[nextIndex] : null;
  const remaining = total > 0 ? checkouts.slice(nextIndex) : [];
  return {
    total,
    paidCount: paid,
    isComplete: total === 0 || nextCheckout == null,
    paidCheckout,
    nextCheckout,
    remaining,
  };
}
